import {
  Avatar,
  Box,
  CircularProgress,
  Grid,
  makeStyles
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import React, { FC, ReactElement } from 'react';
import BasicTable from 'src/components/BasicTable';
import BasicTableColorable from 'src/components/BasicTableColorable';
import Header from 'src/components/Header';
import SimpleAlert from 'src/components/SimpleAlert';
import { useGetCashTransactionItemsQuery } from 'src/features/cashTransactionReport/api/cashTransactionReportApi';
import {
  formatCashierName,
  formatCurrency
} from 'src/features/cashTransactionReport/utils/cashTransactionReport';
import { DateTime } from 'src/features/timezone';
import { useApiErrorMessage } from 'src/hooks/useApiErrorMessage';
import { Theme } from 'src/theme';
import {
  DataDTOOrderHistoryItemList,
  DataDTOTransactionHistory,
  DataEnumerationsItemDiscountType,
  DataEnumerationsItemStatus
} from 'src/types/api/generated/api';

interface CashTransactionDetailProps {
  transaction: DataDTOTransactionHistory;
}

const transactionHeaders = [
  'Store',
  'Station Group',
  'Station',
  'External ID',
  'Start Time',
  'Completed Time',
  'Approval Time',
  'Approved By / Employee',
  'Items Purchased',
  'Amount / Total Price'
];

const itemHeaders = [
  'Name',
  'Description',
  'SKU',
  'EPC',
  'UPC Data',
  'Price',
  'Discount',
  'Status',
  'Capture Method',
  'Icon'
];

const columnWidths = [
  '16%',
  '16%',
  '9%',
  '11%',
  '10%',
  '9%',
  '9%',
  '7%',
  '9%',
  '4%'
];

const useStyles = makeStyles((theme: Theme) => ({
  avatar: {
    height: 42,
    width: 42,
    marginRight: theme.spacing(1)
  }
}));

const formatDiscount = (
  amount?: number | null,
  discountType?: DataEnumerationsItemDiscountType | null
): string => {
  if (typeof amount !== 'number' || amount <= 0) {
    return 'None';
  }
  return discountType === DataEnumerationsItemDiscountType.Percentage
    ? `${amount}%`
    : formatCurrency(amount);
};

const CashTransactionDetail: FC<CashTransactionDetailProps> = ({
  transaction
}) => {
  const theme = useTheme<Theme>();
  const classes = useStyles();
  const {
    data: items = [],
    isFetching,
    error
  } = useGetCashTransactionItemsQuery(transaction.id);
  const errorMessage = useApiErrorMessage(error);

  const getItemIcon = (
    item: DataDTOOrderHistoryItemList
  ): string | ReactElement => {
    if (!item.icon || item.icon === 'n-a' || item.icon === 'Unknown') {
      return 'N/A';
    }
    return (
      <Box display="flex" alignItems="center">
        <Avatar className={classes.avatar} src={item.icon}>
          {item.icon}
        </Avatar>
      </Box>
    );
  };

  const getItemRow = (
    item: DataDTOOrderHistoryItemList,
    deactivated = false
  ) => [
    item.name ?? 'Unknown',
    item.name ?? 'Unknown',
    item.sku ?? '',
    item.primaryIdentifier ?? '',
    item.data ?? '',
    formatCurrency(item.purchasePrice),
    formatDiscount(item.discountAmount, item.discountType),
    deactivated
      ? 'Deactivated'
      : item.voidedFrom
      ? `${item.status} - ${item.voidedFrom}`
      : item.status ?? '',
    item.captureMethod,
    getItemIcon(item)
  ];

  const purchasedItems = items.filter(
    item => item.isActive && item.status !== DataEnumerationsItemStatus.Voided
  );
  const activeItemRows = purchasedItems.map(item => getItemRow(item));
  const voidedItemRows = items
    .filter(
      item => item.isActive && item.status === DataEnumerationsItemStatus.Voided
    )
    .map(item => getItemRow(item));
  const deactivatedItemRows = items
    .filter(item => !item.isActive)
    .map(item => getItemRow(item, true));
  const totalIndividualPrices = purchasedItems.reduce(
    (total, item) => total + (item.purchasePrice ?? 0),
    0
  );
  const totalDiscounts = purchasedItems.reduce(
    (total, item) => total + (item.discountAmount ?? 0),
    0
  );

  const transactionRows = [
    [
      transaction.storeName ?? 'N/A',
      transaction.stationGroupName ?? 'N/A',
      transaction.stationName ?? 'N/A',
      transaction.externalId ?? 'N/A',
      <DateTime value={transaction.startedAt} />,
      transaction.completedAt ? (
        <DateTime value={transaction.completedAt} />
      ) : (
        'N/A'
      ),
      transaction.cashApprovedAt ? (
        <DateTime value={transaction.cashApprovedAt} />
      ) : (
        'N/A'
      ),
      formatCashierName(transaction),
      transaction.validItemCount,
      formatCurrency(transaction.cashCollectedAmount)
    ]
  ];

  return (
    <Grid item xs={12} sm={12} md={12} lg={12}>
      <Header
        breadcrumbs={[]}
        pageTitle="Cash Transaction Detail"
        mainHeader="Reports"
      />
      <Box mt={3}>
        <BasicTable
          dataAlign="center"
          headings={transactionHeaders}
          rows={transactionRows}
        />
      </Box>
      {isFetching ? (
        <Box mt={3} display="flex" justifyContent="center">
          <CircularProgress color="primary" />
        </Box>
      ) : null}
      {error && errorMessage ? (
        <Box mt={3}>
          <SimpleAlert severity="error" text={errorMessage} />
        </Box>
      ) : null}
      {!isFetching && !error ? (
        <>
          {activeItemRows.length ? (
            <Box mt={3}>
              <BasicTableColorable
                dataAlign="center"
                headings={itemHeaders}
                colsWidth={columnWidths}
                rows={activeItemRows}
                oddRowColor={{
                  backGround: theme.customs.transaction.lightGreen,
                  text: theme.customs.textOverLightShades
                }}
                evenRowColor={{
                  backGround: theme.customs.transaction.green,
                  text: theme.customs.textOverLightShades
                }}
              />
            </Box>
          ) : null}
          {voidedItemRows.length ? (
            <Box mt={3}>
              <BasicTableColorable
                dataAlign="center"
                headings={itemHeaders}
                colsWidth={columnWidths}
                rows={voidedItemRows}
                oddRowColor={{
                  backGround: theme.customs.transaction.peachOrange,
                  text: theme.customs.textOverLightShades
                }}
                evenRowColor={{
                  backGround: theme.customs.transaction.orange,
                  text: theme.customs.textOverLightShades
                }}
              />
            </Box>
          ) : null}
          {deactivatedItemRows.length ? (
            <Box mt={3}>
              <BasicTableColorable
                dataAlign="center"
                headings={itemHeaders}
                colsWidth={columnWidths}
                rows={deactivatedItemRows}
                oddRowColor={{
                  backGround: theme.customs.transaction.lightRed,
                  text: theme.customs.textOverLightShades
                }}
                evenRowColor={{
                  backGround: theme.customs.transaction.red,
                  text: theme.customs.textOverLightShades
                }}
              />
            </Box>
          ) : null}
          <Box mt={3}>
            <BasicTable
              dataAlign="center"
              headings={[
                'Total Individual Prices',
                'Total Discounts',
                'Final Total Price'
              ]}
              rows={[
                [
                  formatCurrency(totalIndividualPrices),
                  formatCurrency(totalDiscounts),
                  formatCurrency(transaction.cashCollectedAmount)
                ]
              ]}
            />
          </Box>
        </>
      ) : null}
    </Grid>
  );
};

export default CashTransactionDetail;
